import { api } from './client';

export interface FirebaseAccount {
  id: number;
  name: string;
  ga4_property_id: string;
  sa_client_email: string;
  enabled: number;
  last_synced_at: number | null;
  last_error: string | null;
  web_api_key: string | null;
  web_sender_id: string | null;
  web_app_id: string | null;
  web_vapid_key: string | null;
}

export interface FirebaseSummary {
  total_users: number;
  dau: number;
  wau: number;
  mau: number;
  new_today: number;
  new_yesterday: number;
  crash_free_rate_30d?: number | null;
  crash_affected_users_30d?: number;
}

export interface FirebaseDaily {
  date: string;
  active_users: number;
  new_users: number;
  sessions: number;
  engaged_sessions: number;
  regs: number;
  deps: number;
  crash_affected_users?: number | null;
}

export interface FirebaseTopRow { name: string; users: number; events: number }
export interface FirebaseRetentionRow { cohort_date: string; d1: number; d7: number; d30: number }

export interface FirebaseMetrics {
  summary: FirebaseSummary | null;
  daily: FirebaseDaily[];
  retention: FirebaseRetentionRow[];
  top_events: FirebaseTopRow[];
  top_countries: FirebaseTopRow[];
  top_devices: FirebaseTopRow[];
  app_versions: FirebaseTopRow[];
  last_synced_at: number | null;
  last_error: string | null;
}

const BASE = '/admin/firebase';

export const listAccounts = () => api.get<FirebaseAccount[]>(`${BASE}/accounts`);

export const createAccount = (input: {
  name: string; ga4_property_id: string; sa_json: string;
  web_api_key?: string; web_sender_id?: string; web_app_id?: string; web_vapid_key?: string;
}) => api.post<{ id: number }>(`${BASE}/accounts`, input);

export const updateAccount = (id: number, input: Partial<{
  name: string; enabled: boolean; ga4_property_id: string; sa_json: string;
  web_api_key: string; web_sender_id: string; web_app_id: string; web_vapid_key: string;
}>) => api.patch<{ id: number }>(`${BASE}/accounts/${id}`, input);

export const deleteAccount = (id: number) =>
  api.delete<{ id: number }>(`${BASE}/accounts/${id}`);

export const refreshAccount = (id: number) =>
  api.post<{ queued: boolean }>(`${BASE}/accounts/${id}/refresh`);

export const getMetrics = (id: number, range: 7 | 30 | 90 = 30) =>
  api.get<FirebaseMetrics>(`${BASE}/accounts/${id}/metrics?range=${range}`);

export interface FirebaseModerationRow { country: string; total: number; blocked: number }

export const getModeration = (id: number) =>
  api.get<FirebaseModerationRow[]>(`${BASE}/accounts/${id}/moderation`);

// ---------- Organisations (project factories) ----------

export interface FirebaseOrg {
  id: number;
  name: string;
  parent: string;               // "organizations/123" | "folders/456"
  billing_account: string | null;
  analytics_account_id: string;
  sa_client_email: string;
  enabled: number;
  max_projects: number;         // slots; a full organisation leaves the Apps Setup picker
  last_error: string | null;
  created_at: number;
  projects: number;             // provisioning runs holding a slot (created or about to be)
}

export interface FirebaseProvision {
  id: number;
  org_id: number;
  org_name: string;
  import_id: number | null;
  app_id: string | null;         // started from an Apps row (no import)
  bundle_id: string;
  display_name: string;
  project_id: string;
  state: 'new' | 'project' | 'firebase' | 'analytics' | 'services' | 'sa' | 'ready';
  ga4_property_id: string | null;
  sa_client_email: string | null;
  account_id: number | null;
  error: string | null;
  created_at: number;
  updated_at: number;
}

export const listOrgs = () => api.get<FirebaseOrg[]>(`${BASE}/orgs`);
export const createOrg = (input: {
  name: string; parent: string; billing_account?: string; analytics_account_id: string; sa_json: string;
}) => api.post<{ id: number }>(`${BASE}/orgs`, input);
export const updateOrg = (id: number, input: Partial<{
  name: string; enabled: boolean; billing_account: string; analytics_account_id: string; max_projects: number;
}>) => api.patch<{ id: number }>(`${BASE}/orgs/${id}`, input);
export const deleteOrg = (id: number) => api.delete<{ id: number }>(`${BASE}/orgs/${id}`);

export const listProvisions = () => api.get<FirebaseProvision[]>(`${BASE}/provisions`);
export const getProvision = (id: number) => api.get<FirebaseProvision>(`${BASE}/provisions/${id}`);
export const retryProvision = (id: number) => api.post<{ id: number; queued: boolean }>(`${BASE}/provisions/${id}/retry`);

// Where a browser-profile run stopped (the runner's screenshot).
export const getProvisionEvidence = (id: number) => api.blob(`/admin/firebase/provisions/${id}/evidence`);
